import FastfoodIcon from '@mui/icons-material/Fastfood';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ReactComponent as ApparelIcon } from '../../assets/apparel_24px.svg';
import { EventTicketData } from '../../types';
import { mealNote, mealTypeOptions, shirtSizeOptions } from './utils';

export function RegisterSelections(props: {
  ticket: EventTicketData | undefined;
  ticketCount: number;
  shirtSizes: { [key: number]: string };
  onChangeShirt: (key: number, shirt: string) => void;
  mealTypes: { [key: number]: string };
  onChangeMeal: (key: number, meal: string) => void;
}) {
  // Props
  const { ticket, shirtSizes, mealTypes } = props;

  const hasRequirements =
    ticket && (ticket.ReqShirtSize === 1 || ticket.ReqMealType === 1);

  if (hasRequirements && props.ticketCount > 0) {
    const reqShirt: boolean = ticket.ReqShirtSize === 1;
    const reqMeal: boolean = ticket.ReqMealType === 1;

    return (
      <Stack spacing={1}>
        <Typography variant="h6" component="div">
          Make Selections for Tickets
        </Typography>

        {Array.from({ length: props.ticketCount }).map((_, index) => {
          const selectedSize = shirtSizes[index] || '';
          const selectedMeal = mealTypes[index] || '';

          return (
            <Stack key={index} spacing={1}>
              <Box
                sx={{
                  fontFamily: 'Roboto Mono',
                  fontWeight: 'bold',
                  fontSize: 14,
                  textTransform: 'uppercase',
                  color: '#4a4a4a',
                }}
              >
                Ticket {index + 1}
              </Box>

              {reqShirt ? (
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid #0002',
                    borderLeft: '4px solid red',
                    borderRadius: '0 4px 4px 0',
                    p: 1,
                  }}
                >
                  <Stack
                    spacing={1}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Stack
                      spacing={1}
                      direction="row"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Box
                        sx={{
                          fontSize: 20,
                          fontFamily: 'Roboto Mono',
                          fontWeight: 'bold',
                          color: '#9b9b9b',
                        }}
                      >
                        {index + 1}
                      </Box>

                      <ApparelIcon />

                      <Box>Select shirt size</Box>

                      <Box
                        sx={{
                          flex: 1,
                          borderTop: '2px dashed #0001',
                        }}
                      />
                    </Stack>

                    <FormControl
                      size="small"
                      sx={{
                        width: { xs: '100%', sm: 'inherit' },
                        minWidth: 120,
                      }}
                    >
                      <InputLabel id={`shirt-size-select-label`}>
                        Shirt size
                      </InputLabel>
                      <Select
                        labelId={`shirt-size-select-label`}
                        id={`shirt-size-select`}
                        label="Shirt size"
                        value={selectedSize}
                        onChange={(event) => {
                          const newSize = event.target.value;
                          props.onChangeShirt(index, newSize);
                        }}
                        error={selectedSize === ''}
                      >
                        {shirtSizeOptions.map((sizeOption) => {
                          return (
                            <MenuItem
                              key={sizeOption}
                              value={sizeOption}
                              sx={{
                                fontFamily: 'Roboto Mono',
                                fontWeight: 'bold',
                              }}
                            >
                              {sizeOption}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>
              ) : null}

              {reqMeal ? (
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid #0002',
                    borderLeft: '4px solid red',
                    borderRadius: '0 4px 4px 0',
                    p: 1,
                  }}
                >
                  <Stack
                    spacing={1}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Stack
                      spacing={1}
                      direction="row"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Box
                        sx={{
                          fontSize: 20,
                          fontFamily: 'Roboto Mono',
                          fontWeight: 'bold',
                          color: '#9b9b9b',
                        }}
                      >
                        {index + 1}
                      </Box>

                      <FastfoodIcon />

                      <Box>Select meal preference</Box>

                      <Box
                        sx={{
                          flex: 1,
                          borderTop: '2px dashed #0001',
                        }}
                      />
                    </Stack>

                    <FormControl
                      size="small"
                      sx={{
                        width: { xs: '100%', sm: 'inherit' },
                        minWidth: 160,
                      }}
                    >
                      <InputLabel id={`meal-type-select-label`}>
                        Meal preference
                      </InputLabel>
                      <Select
                        labelId={`meal-type-select-label`}
                        id={`meal-type-select`}
                        label="Meal preference"
                        value={selectedMeal}
                        onChange={(event) => {
                          const newMeal = event.target.value;
                          props.onChangeMeal(index, newMeal);
                        }}
                        error={selectedMeal === ''}
                      >
                        {mealTypeOptions.map((mealOption) => {
                          return (
                            <MenuItem
                              key={mealOption.value}
                              value={mealOption.value}
                            >
                              <Stack>
                                <Box
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: 14,
                                  }}
                                >
                                  {mealOption.value}
                                </Box>
                                <Box
                                  sx={{
                                    fontStyle: 'italic',
                                    fontSize: 12,
                                    color: '#4a4a4a',
                                  }}
                                >
                                  {mealOption.description}
                                </Box>
                              </Stack>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          );
        })}

        {Object.values(mealTypes).includes('Special Meal') ? (
          <Alert severity="warning">
            <AlertTitle>Important Note</AlertTitle>
            {mealNote}
          </Alert>
        ) : null}
      </Stack>
    );
  } else {
    return null;
  }
}
